import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  FileSpreadsheet, FileText, TrendingUp, BookOpen, Receipt, Clock, type LucideIcon,
} from 'lucide-react';
import { Header } from '@/components/layout/Header';
import { useAuthStore } from '@/stores/authStore';
import { useFinanceKpisHistory } from '@/hooks/useFinanceKpis';
import { useJournal } from '@/hooks/useJournal';
import { useInvoices } from '@/hooks/useInvoices';
import { financeApi, type FinanceKpis, type BalanceAgeeClient } from '@/api/finance';
import { exportToXLSX, exportToPDF, buildExportFilename, type ExportColumn } from '@/utils/exports';
import type { Role, JournalEntry, Invoice } from '@/types';

/**
 * Rapports.
 *
 * La route /reporting affichait jusqu'ici un écran « à venir ». Les données
 * étaient pourtant déjà là : cette page ne crée aucun endpoint, elle rassemble
 * en un seul endroit des exports qui n'existaient que dispersés dans cinq
 * écrans — et pour la balance âgée, nulle part.
 *
 * Chaque bloc est MONTÉ selon le rôle : un hook ne doit pas interroger un
 * endpoint que le rôle n'a pas le droit d'appeler.
 *
 * Ce que cette page ne fait PAS, volontairement : l'historique des rapports
 * générés, qui demanderait de stocker les fichiers côté serveur. Mieux vaut
 * quatre exports qui fonctionnent qu'une promesse de plus.
 */

const ROLES_COMPTA:   Role[] = ['daf', 'dg_filiale', 'admin_holding', 'auditeur'];
const ROLES_FACTURES: Role[] = ['daf', 'dg_filiale', 'admin_holding', 'auditeur', 'manager', 'commercial', 'acheteur'];
const ROLES_BALANCE:  Role[] = ['daf', 'dg_filiale', 'admin_holding', 'auditeur', 'manager'];

const enFrancs = (cents: string | undefined) => (cents ? Number(BigInt(cents)) / 100 : 0);
const jour = (iso: string | undefined) => (iso ? new Date(iso).toLocaleDateString('fr-FR') : '—');

export const ReportingPage = () => {
  const role    = useAuthStore((s) => s.user?.role) as Role | undefined;
  const filiale = useAuthStore((s) => s.filiale);

  const voitCompta   = !!role && ROLES_COMPTA.includes(role);
  const voitFactures = !!role && ROLES_FACTURES.includes(role);
  const voitBalance  = !!role && ROLES_BALANCE.includes(role);

  const aucunRapport = !voitCompta && !voitFactures && !voitBalance;

  return (
    <>
      <Header
        title="Rapports"
        subtitle={`Exports comptables et commerciaux · ${filiale?.fullName ?? ''}`}
      />

      <div className="p-6">
        {aucunRapport ? (
          <p className="text-sm" style={{ color: 'var(--tx-2)' }}>
            Aucun rapport n'est disponible pour votre rôle.
          </p>
        ) : (
          <>
            <p className="mb-5 text-sm" style={{ color: 'var(--tx-2)' }}>
              Chaque rapport s'exporte au format Excel (exploitable, une ligne par
              enregistrement) ou PDF (mis en page, prêt à transmettre).
            </p>

            <div className="grid gap-4 lg:grid-cols-2">
              {voitCompta   && <RapportSituation />}
              {voitCompta   && <RapportJournal />}
              {voitFactures && <RapportFactures />}
              {voitBalance  && <RapportBalanceAgee />}
            </div>
          </>
        )}
      </div>
    </>
  );
};

/* ── Situation financière : la série mensuelle des KPI ─────────────────── */
const RapportSituation = () => {
  const { data, isLoading } = useFinanceKpisHistory(12);
  const filiale = useAuthStore((s) => s.filiale);
  const lignes  = data?.series ?? [];

  const colonnes: ExportColumn<FinanceKpis>[] = [
    { header: 'Période',            accessor: (k) => k.periode },
    { header: "Chiffre d'affaires", accessor: (k) => enFrancs(k.caCents),           align: 'right' },
    { header: 'Charges',            accessor: (k) => enFrancs(k.chargesCents),      align: 'right' },
    { header: 'Marge brute',        accessor: (k) => enFrancs(k.margeBruteCents),   align: 'right' },
    { header: 'EBITDA',             accessor: (k) => enFrancs(k.ebitdaCents),       align: 'right' },
    { header: 'Trésorerie nette',   accessor: (k) => enFrancs(k.tresorerieCents),   align: 'right' },
    { header: 'BFR',                accessor: (k) => enFrancs(k.bfrCents),          align: 'right' },
    { header: 'DSO (jours)',        accessor: (k) => k.dsoDays,                     align: 'right' },
  ];

  return (
    <CarteRapport
      icone={TrendingUp}
      titre="Situation financière"
      description="Les indicateurs mois par mois sur un an : chiffre d'affaires, charges, marge, EBITDA, trésorerie, BFR et délai de paiement client."
      compte={isLoading ? null : `${lignes.length} mois`}
      onXLSX={() => exportToXLSX({
        filename:  buildExportFilename('Situation-financiere', filiale?.filialeKey, 'xlsx'),
        sheetName: 'KPI mensuels',
        columns:   colonnes,
        rows:      lignes,
      })}
      onPDF={() => exportToPDF({
        title:       `Situation financière — ${filiale?.fullName ?? ''}`,
        subtitle:    `SYSCOHADA révisé · ${lignes.length} mois`,
        filename:    buildExportFilename('Situation-financiere', filiale?.filialeKey, 'pdf'),
        orientation: 'landscape',
        columns:     colonnes,
        rows:        lignes,
      })}
      vide={!isLoading && lignes.length === 0}
      messageVide="Aucun indicateur calculé pour le moment."
    />
  );
};

/* ── Journal comptable ─────────────────────────────────────────────────── */
const RapportJournal = () => {
  const { data, isLoading } = useJournal({ limit: 500 });
  const filiale = useAuthStore((s) => s.filiale);
  const lignes  = data?.data ?? [];

  const colonnes: ExportColumn<JournalEntry>[] = [
    { header: 'Date',        accessor: (e) => jour(e.entryDate) },
    { header: 'Référence',   accessor: (e) => e.reference },
    { header: 'Journal',     accessor: (e) => e.journalType },
    { header: 'Description', accessor: (e) => e.description ?? '' },
    { header: 'Statut',      accessor: (e) => e.status },
    { header: 'Débit',       accessor: (e) => e.lines.reduce((s, l) => s + Number(BigInt(l.debitCents)), 0) / 100,  align: 'right' },
    { header: 'Crédit',      accessor: (e) => e.lines.reduce((s, l) => s + Number(BigInt(l.creditCents)), 0) / 100, align: 'right' },
  ];

  return (
    <CarteRapport
      icone={BookOpen}
      titre="Journal comptable"
      description="Toutes les écritures avec leurs totaux débit et crédit, conformes au plan SYSCOHADA révisé."
      compte={isLoading ? null : `${lignes.length} écriture${lignes.length > 1 ? 's' : ''}`}
      onXLSX={() => exportToXLSX({
        filename:  buildExportFilename('Journal', filiale?.filialeKey, 'xlsx'),
        sheetName: 'Journal SYSCOHADA',
        columns:   colonnes,
        rows:      lignes,
      })}
      onPDF={() => exportToPDF({
        title:       `Journal comptable — ${filiale?.fullName ?? ''}`,
        subtitle:    `SYSCOHADA révisé · ${lignes.length} écriture${lignes.length > 1 ? 's' : ''}`,
        filename:    buildExportFilename('Journal', filiale?.filialeKey, 'pdf'),
        orientation: 'landscape',
        columns:     colonnes,
        rows:        lignes,
      })}
      vide={!isLoading && lignes.length === 0}
      messageVide="Aucune écriture enregistrée."
    />
  );
};

/* ── Factures ──────────────────────────────────────────────────────────── */
const RapportFactures = () => {
  const { data, isLoading } = useInvoices({ limit: 500 });
  const filiale = useAuthStore((s) => s.filiale);
  const lignes  = data?.data ?? [];

  const colonnes: ExportColumn<Invoice>[] = [
    { header: 'Référence', accessor: (f) => f.reference },
    { header: 'Type',      accessor: (f) => f.invoiceType },
    { header: 'Émission',  accessor: (f) => jour(f.issueDate) },
    { header: 'Échéance',  accessor: (f) => jour(f.dueDate) },
    { header: 'Statut',    accessor: (f) => f.status },
    { header: 'Total TTC', accessor: (f) => enFrancs(f.totalCents),      align: 'right' },
    { header: 'Réglé',     accessor: (f) => enFrancs(f.paidAmountCents), align: 'right' },
    { header: 'Reste dû',  accessor: (f) => enFrancs(f.totalCents) - enFrancs(f.paidAmountCents), align: 'right' },
  ];

  return (
    <CarteRapport
      icone={Receipt}
      titre="Factures"
      description="Factures clients et fournisseurs, avec le montant réglé et le reste dû par document."
      compte={isLoading ? null : `${lignes.length} document${lignes.length > 1 ? 's' : ''}`}
      onXLSX={() => exportToXLSX({
        filename:  buildExportFilename('Factures', filiale?.filialeKey, 'xlsx'),
        sheetName: 'Factures',
        columns:   colonnes,
        rows:      lignes,
      })}
      onPDF={() => exportToPDF({
        title:       `Factures — ${filiale?.fullName ?? ''}`,
        subtitle:    `${lignes.length} document${lignes.length > 1 ? 's' : ''}`,
        filename:    buildExportFilename('Factures', filiale?.filialeKey, 'pdf'),
        orientation: 'landscape',
        columns:     colonnes,
        rows:        lignes,
      })}
      vide={!isLoading && lignes.length === 0}
      messageVide="Aucune facture enregistrée."
    />
  );
};

/* ── Balance âgée : le seul de ces rapports qui n'était exportable nulle part ── */
const RapportBalanceAgee = () => {
  const filiale = useAuthStore((s) => s.filiale);
  const { data, isLoading } = useQuery({
    queryKey: ['finance', 'balance-agee'],
    queryFn:  () => financeApi.getBalanceAgee(),
    staleTime: 60_000,
  });
  const lignes = data?.parClient ?? [];

  const colonnes: ExportColumn<BalanceAgeeClient>[] = [
    { header: 'Client',      accessor: (c) => c.nom },
    { header: 'Factures',    accessor: (c) => c.nbFactures,        align: 'right' },
    { header: 'Encours',     accessor: (c) => enFrancs(c.total),   align: 'right' },
    { header: 'À jour',      accessor: (c) => enFrancs(c.a_jour),  align: 'right' },
    { header: '0-30 j',      accessor: (c) => enFrancs(c.j_0_30),  align: 'right' },
    { header: '30-60 j',     accessor: (c) => enFrancs(c.j_30_60), align: 'right' },
    { header: '60-90 j',     accessor: (c) => enFrancs(c.j_60_90), align: 'right' },
    { header: 'Plus de 90 j', accessor: (c) => enFrancs(c.plus_90), align: 'right' },
  ];

  return (
    <CarteRapport
      icone={Clock}
      titre="Balance âgée clients"
      description="Les créances client réparties par ancienneté. Le seul de ces rapports qui n'était exportable depuis aucun écran."
      compte={isLoading ? null : `${lignes.length} client${lignes.length > 1 ? 's' : ''}`}
      onXLSX={() => exportToXLSX({
        filename:  buildExportFilename('Balance-agee', filiale?.filialeKey, 'xlsx'),
        sheetName: 'Balance âgée',
        columns:   colonnes,
        rows:      lignes,
      })}
      onPDF={() => exportToPDF({
        title:       `Balance âgée clients — ${filiale?.fullName ?? ''}`,
        subtitle:    data?.asOf ? `Arrêtée au ${jour(data.asOf)}` : undefined,
        filename:    buildExportFilename('Balance-agee', filiale?.filialeKey, 'pdf'),
        orientation: 'landscape',
        columns:     colonnes,
        rows:        lignes,
      })}
      vide={!isLoading && lignes.length === 0}
      messageVide="Aucune créance client en cours."
    />
  );
};

/* ── Présentation commune ──────────────────────────────────────────────── */
const CarteRapport = ({
  icone: Icone, titre, description, compte, onXLSX, onPDF, vide, messageVide,
}: {
  icone: LucideIcon;
  titre: string;
  description: ReactNode;
  /** null pendant le chargement. */
  compte: string | null;
  onXLSX: () => void;
  onPDF: () => void;
  vide: boolean;
  messageVide: string;
}) => (
  <div className="rounded-xl border p-5" style={{ borderColor: 'var(--bdr)', background: '#fff' }}>
    <div className="flex items-start gap-3">
      <span
        className="flex size-10 shrink-0 items-center justify-center rounded-lg"
        style={{ background: 'var(--p-soft)', color: 'var(--p)' }}
      >
        <Icone className="size-5" />
      </span>
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold" style={{ color: 'var(--tx)' }}>{titre}</h2>
        <p className="mt-1 text-sm leading-relaxed" style={{ color: 'var(--tx-2)' }}>{description}</p>
      </div>
    </div>

    <div className="mt-4 flex flex-wrap items-center gap-3">
      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--tx-3)' }}>
        {compte === null ? 'Chargement…' : vide ? messageVide : compte}
      </span>

      <span className="ml-auto flex gap-2">
        <button
          type="button"
          className="ds-btn ds-btn-g ds-btn-sm"
          onClick={() => void onXLSX()}
          disabled={compte === null || vide}
          title={vide ? messageVide : 'Exporter en Excel'}
        >
          <FileSpreadsheet size={14} /> Excel
        </button>
        <button
          type="button"
          className="ds-btn ds-btn-g ds-btn-sm"
          onClick={() => void onPDF()}
          disabled={compte === null || vide}
          title={vide ? messageVide : 'Exporter en PDF'}
        >
          <FileText size={14} /> PDF
        </button>
      </span>
    </div>
  </div>
);
